import { useCallback, useEffect, useMemo, useState } from "react";
import { ActivityMeasurement } from "../types/post";
import { ActivityType } from "../types/activity";
import { activityService } from "../services/activityService";
import { GlobalActivityService } from "../services/globalActivityService";
import { useAuth } from "../contexts/AuthContext";

// Module-level caches keep activity name lookups cheap across cards.
const userActivityTypesCache = new Map<string, ActivityType[]>();
const userActivityTypesInflight = new Map<string, Promise<ActivityType[]>>();
const globalActivityTypesCache = new Map<string, ActivityType[]>();
const globalActivityTypesInflight = new Map<string, Promise<ActivityType[]>>();

const getRawActivityId = (activityTypeId: string): string => {
  if (activityTypeId.startsWith("global_")) {
    const parts = activityTypeId.split("_");
    if (parts.length >= 3) {
      return parts.slice(2).join("_");
    }
  }
  return activityTypeId;
};

const getGlobalCategoryIdFromActivityTypeId = (
  activityTypeId: string,
): string | undefined => {
  if (!activityTypeId.startsWith("global_")) return undefined;
  const parts = activityTypeId.split("_");
  if (parts.length >= 3) {
    return parts[1];
  }
  return undefined;
};

async function loadUserActivityTypes(userId: string): Promise<ActivityType[]> {
  const cached = userActivityTypesCache.get(userId);
  if (cached) return cached;
  let inflight = userActivityTypesInflight.get(userId);
  if (!inflight) {
    inflight = activityService
      .fetchActivityTypes(userId)
      .then((types) => {
        userActivityTypesCache.set(userId, types);
        return types;
      })
      .finally(() => {
        userActivityTypesInflight.delete(userId);
      });
    userActivityTypesInflight.set(userId, inflight);
  }
  return inflight;
}

async function loadGlobalActivityTypes(
  categoryId: string,
): Promise<ActivityType[]> {
  const cached = globalActivityTypesCache.get(categoryId);
  if (cached) return cached;
  let inflight = globalActivityTypesInflight.get(categoryId);
  if (!inflight) {
    inflight = GlobalActivityService.fetchActivityTypes(categoryId)
      .then((types) => {
        globalActivityTypesCache.set(categoryId, types);
        return types;
      })
      .finally(() => {
        globalActivityTypesInflight.delete(categoryId);
      });
    globalActivityTypesInflight.set(categoryId, inflight);
  }
  return inflight;
}

interface UseActivityNameResolverResult {
  getActivityName: (measurement: ActivityMeasurement) => string;
  loading: boolean;
}

/**
 * Resolves user-defined and global pack activity names for a set of
 * ActivityMeasurements. Loads the owning user's activity types once and
 * lazily fetches any global activity categories referenced by the
 * measurements. Caches at module level so repeated use across feed cards
 * does not refetch.
 */
export function useActivityNameResolver(
  measurements: ActivityMeasurement[] | undefined,
  ownerUserId?: string,
): UseActivityNameResolverResult {
  const { currentUser } = useAuth();
  const effectiveUserId = ownerUserId || currentUser?.uid;

  const requiredCategoryIds = useMemo(() => {
    const ids = new Set<string>();
    (measurements || []).forEach((m) => {
      const categoryId =
        m.globalCategoryID ||
        getGlobalCategoryIdFromActivityTypeId(m.ActivityTypeID);
      if (categoryId) ids.add(categoryId);
    });
    return Array.from(ids);
  }, [measurements]);

  const [userActivityTypes, setUserActivityTypes] = useState<ActivityType[]>(
    () =>
      effectiveUserId
        ? userActivityTypesCache.get(effectiveUserId) || []
        : [],
  );
  const [globalActivityTypes, setGlobalActivityTypes] = useState<
    Record<string, ActivityType[]>
  >(() => {
    const initial: Record<string, ActivityType[]> = {};
    requiredCategoryIds.forEach((id) => {
      const cached = globalActivityTypesCache.get(id);
      if (cached) initial[id] = cached;
    });
    return initial;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!effectiveUserId) return;
    let cancelled = false;
    const cached = userActivityTypesCache.get(effectiveUserId);
    if (cached) {
      setUserActivityTypes(cached);
      return;
    }
    setLoading(true);
    loadUserActivityTypes(effectiveUserId)
      .then((types) => {
        if (!cancelled) setUserActivityTypes(types);
      })
      .catch(() => {
        if (!cancelled) setUserActivityTypes([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveUserId]);

  useEffect(() => {
    if (requiredCategoryIds.length === 0) return;
    let cancelled = false;
    const missing = requiredCategoryIds.filter(
      (id) => !globalActivityTypesCache.has(id),
    );
    if (missing.length === 0) {
      setGlobalActivityTypes((prev) => {
        const next = { ...prev };
        requiredCategoryIds.forEach((id) => {
          const cached = globalActivityTypesCache.get(id);
          if (cached && !next[id]) next[id] = cached;
        });
        return next;
      });
      return;
    }
    setLoading(true);
    Promise.all(missing.map((id) => loadGlobalActivityTypes(id)))
      .then((results) => {
        if (cancelled) return;
        setGlobalActivityTypes((prev) => {
          const next = { ...prev };
          missing.forEach((id, idx) => {
            next[id] = results[idx];
          });
          requiredCategoryIds.forEach((id) => {
            const cached = globalActivityTypesCache.get(id);
            if (cached && !next[id]) next[id] = cached;
          });
          return next;
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [requiredCategoryIds]);

  const getActivityName = useCallback(
    (measurement: ActivityMeasurement): string => {
      const rawActivityId = getRawActivityId(measurement.ActivityTypeID);

      const userActivityType = userActivityTypes.find(
        (type) => type.id === rawActivityId,
      );
      if (userActivityType) return userActivityType.name;

      const categoryId =
        measurement.globalCategoryID ||
        getGlobalCategoryIdFromActivityTypeId(measurement.ActivityTypeID);
      if (categoryId) {
        const categoryTypes = globalActivityTypes[categoryId] || [];
        const packActivityType = categoryTypes.find(
          (type) => type.id === rawActivityId,
        );
        if (packActivityType) return packActivityType.name;
      }

      for (const categoryTypes of Object.values(globalActivityTypes)) {
        const match = categoryTypes.find((type) => type.id === rawActivityId);
        if (match) return match.name;
      }

      return "Unknown Activity";
    },
    [userActivityTypes, globalActivityTypes],
  );

  return { getActivityName, loading };
}
